import * as React from 'react'
import {
  TimesheetActionBar,
  TimesheetTable,
  CalculateRecordsModal,
  CalculateRecordsView,
  TimesheetViewMode,
} from '../components'
import { useDrivers } from '@/modules/shared/context/drivers/DriversContext'
import { useTable } from '@/modules/drivers/context/TableContext'
import {
  useLoadingStore,
  selectLoading,
} from '@/modules/shared/stores/loadingStore'
import type { GroupProps } from './Group.types'
import { normalizeMonthKey, formatDateFromMonth } from './Group.utils'
import { useMonthSync, useGroupedTimesheets } from './Group.hooks'
import { useGroupHandlers } from './Group.handlers'
import { YearMonthsValue } from '@/modules/drivers/types/date'
import { useDriversGroupTimesheetStore } from '@/modules/drivers/stores/driversGroupTimesheetStore'
import { useDate } from '@/modules/drivers/stores/dateStore'
import { ReportType } from '@/app/constants/reportType'
import { PrintActionContainer } from '@/modules/printouts/containers/PrintActionContainer'
import { TimesheetPrintShortcuts } from '@/modules/printouts/components/TimesheetPrintShortcuts'
import { resolveDate } from '@/modules/shared/utils/resolveDate'

import { useRemoveTimesheets } from '@/modules/drivers/hooks/useRemoveTimesheets'
import { ConfirmModal } from '@/modules/drivers/modals/ConfirmModal'
import { useForeignTripConfirm } from './useForeignTripConfirm'
import { useOutOfDateRateConfirm } from './useOutOfDateRateConfirm'
import { toast } from '@/lib/toasts/api'
import { useLocalize } from '@/modules/shared/context/localize/LocalizeContext'
import { useTask } from '@/modules/shared/context/TaskContext'
import { TaskType } from '@/app/constants/taskType'
import { TaskStatus } from '@/app/constants/taskStatus'
import { Loader } from '@/ui'

import TimeSheetActionBar from '@/modules/drivers/shared/actionBars/TimeSheetActionBar'
import { useSideSelectionStore } from '@/modules/drivers/stores/sideSelectionStore'
import { useQueryClient } from '@tanstack/react-query'
import { fetchExpectedNettoFromEasyTms } from '@/lib/api/company/timesheet/groupedTimesheet/fetchExpectedNettoFromEasyTms'
import { getGroupedTimesheet } from '@/lib/api/company/timesheet/groupedTimesheet/getGroupedTimesheet'
import { PayrollDriverRow } from '@/lib/api/company/timesheet/groupedTimesheet/types'
import { pickReward } from './payroll/utils'
import { useSaveAndCalculate } from './payroll/hooks/useSaveAndCalculate'
import { useBonusRewardsToolbar } from './payroll/hooks/useBonusRewardsToolbar'

const availableReports = [
  ReportType.TIMESHEET_FOREIGN_AMOUNTS_REPORT,
  ReportType.TIMESHEET_LUMPSUM_SETTLEMENT,
  ReportType.TIMESHEET_LUMPSUM_UTILIZATION,
  ReportType.TIMESHEET_APPLICATION_FOR_LEAVE_FOR_DRIVERS,
  ReportType.TIMESHEET_REWARD_FOR_DRIVERS,
  ReportType.TIMESHEET,
  ReportType.TIMESHEET_LEAVES_CALCULATION,
  ReportType.TIMESHEET_LEAVE_EQUIVALENT_CALCULATION,
  ReportType.REWARDS_TO_ACCEPT_REPORT,
  ReportType.MOBILITY_PACKAGE_PAYROL_REPORT,
  ReportType.TRIP_COSTS,
  ReportType.TIMESHEET_AMOUNTS_REPORT,
  ReportType.TIMESHEET_DETAILED_WAGE_REPORT,
  ReportType.TIMESHEET_YEAR_SUMMARY,
  ReportType.TIMESHEET_FOREIGN_AMOUNT_SUMMARY,
  ReportType.TIMESHEET_LUMPSUM_SETTLEMENT_SUMMARY,
  ReportType.TIMESHEET_WORK_COSTS,
  ReportType.TIMESHEET_PAYMENT_REPORT,
  ReportType.DRIVER_WORKING_DAYS,
  ReportType.TIMESHEET_LEAVE_DAYS_REPORT,
  // ReportType.SELECTED_TIMESHEET_MOBILITY_PACKAGE_PAYROLL_REPORT,
  // ReportType.SELECTED_REWARDS_TO_ACCEPT_REPORT,
]

const GroupComponent: React.FC<GroupProps> = ({ companyId }) => {
  const { drivers, search } = useDrivers()
  const loading = useLoadingStore(selectLoading)
  const { selectedGroup, isSelectedAny, resetAll } = useTable()
  const { sideCheckedIds } = useSideSelectionStore()
  const { removeTimesheets, isPending: isRemovingTimesheets, statusText: removalStatusText } = useRemoveTimesheets()
  const { t } = useLocalize()
  const { tasks, completedTasks } = useTask()

  // Licznik postepu dla sciezki zadaniowej (kilka miesiecy / brak domykania
  // nagrod): polling zadan niesie progressCurrent/progressTotal — te same
  // liczby, ktore useSaveAndCalculate dokleja na sciezce jednomiesiecznej.
  // Bez tego overlay pokazywal goly napis i wygladal na zawieszony.
  const runningCalculationProgress = React.useMemo(() => {
    const running = tasks.find(
      (task) =>
        task.type === TaskType.Timesheet &&
        task.status !== TaskStatus.Completed &&
        task.status !== TaskStatus.Error,
    )
    if (!running?.progressTotal) return ''
    return ` ${running.progressCurrent ?? 0}/${running.progressTotal}`
  }, [tasks])
  const queryClient = useQueryClient()

  const [isCalculating, setIsCalculating] = React.useState(false)

  const viewedYear = useDriversGroupTimesheetStore((s) => s.viewedYear)

  const [period, setPeriod] = React.useState<YearMonthsValue>({
    year: viewedYear,
    months: [],
  })

  const [viewMode, setViewMode] =
    React.useState<TimesheetViewMode>('group-view')
  const [showBonusFields, setShowBonusFields] = React.useState<boolean>(false)
  const [selectedMonthKey, setSelectedMonthKey] = React.useState<string>(
    `${viewedYear}-01`,
  )

  const [showCalculateRecordsModal, setShowCalculateRecordsModal] =
    React.useState(false)
  const [showCalculateView, setShowCalculateView] = React.useState(false)
  const [showPrintModal, setShowPrintModal] = React.useState(false)
  const [showDeleteModal, setShowDeleteModal] = React.useState(false)
  const [showOnlySelected, setShowOnlySelected] = React.useState(false)
  const [missingContracts, setMissingContracts] = React.useState<{
    list: { driverId: number; driverName: string; months: number[] }[]
    allMissing: boolean
  } | null>(null)

  React.useEffect(() => {
    if (!isSelectedAny) setShowOnlySelected(false)
  }, [isSelectedAny])

  useMonthSync(selectedMonthKey, setSelectedMonthKey)

  const { setYear: setDateContextYear, year } = useDate()
  React.useEffect(() => {
    if (viewedYear) {
      setDateContextYear(String(viewedYear))
    }
  }, [viewedYear])

  const { data, setData, fetchGroupedTimesheets, refreshRows } = useGroupedTimesheets(
    companyId,
    `${viewedYear}-01`,
    showBonusFields,
    sideCheckedIds,
  )

  React.useEffect(() => {
    const done = completedTasks.some(
      (task) =>
        task.type === TaskType.Timesheet &&
        (task.status === TaskStatus.Completed ||
          task.status === TaskStatus.Error),
    )
    if (done) {
      setIsCalculating(false)
      fetchGroupedTimesheets()
      // Amounts preview serves persisted TimesheetAmounts — stale until the job wrote them.
      queryClient.invalidateQueries({ queryKey: ['amounts-preview'] })
      queryClient.invalidateQueries({ queryKey: ['foreign-amounts-preview'] })
      // Przeliczenie kasuje flagi nieaktualnosci - znaczniki zadan przy kierowcach
      // musza zniknac razem z zakonczeniem zadania, bez odswiezania przegladarki.
      queryClient.invalidateQueries({
        predicate: (query) => query.queryKey[0] === 'driverRequiredActions',
      })
    }
  }, [completedTasks, queryClient])

  const filteredData = React.useMemo(() => {
    let result = data
    if (search && search.trim()) {
      const matchingIds = new Set(drivers.map((d) => String(d.id)))
      result = result.filter((row) => matchingIds.has(String(row.id)))
    }
    if (showOnlySelected && selectedGroup?.ids.length) {
      const selectedIds = new Set(selectedGroup.ids.map(String))
      result = result.filter((row) => selectedIds.has(String(row.id)))
    }
    if (sideCheckedIds.length > 0) {
      const sideIds = new Set(sideCheckedIds.map(String))
      result = result.filter((row) => sideIds.has(String(row.id)))
    } else {
      return []
    }
    return result
  }, [data, drivers, search, showOnlySelected, selectedGroup, sideCheckedIds])

  const {
    requestConfirm: confirmForeignActivityWithoutTrip,
    modal: foreignTripConfirmModal,
  } = useForeignTripConfirm()

  const {
    requestConfirm: confirmOutOfDateExchangeRate,
    modal: outOfDateRateConfirmModal,
  } = useOutOfDateRateConfirm()

  const {
    handleCalculate,
    scopeBusy,
    handlePrint,
    handleDriverClick,
    handleRefresh,
    handleCalculateSuccess,
    handleImportFile,
    handleCalculateFromData,
  } = useGroupHandlers({
    confirmOutOfDateExchangeRate,
    confirmForeignActivityWithoutTrip,
    companyId,
    selectedMonthKey,
    period,
    selectedGroup,
    sideCheckedIds,
    drivers,
    setData,
    setShowPrintModal,
    setShowBonusFields,
    setShowCalculateRecordsModal,
    setShowCalculateView,
    setViewMode,
    fetchGroupedTimesheets,
    refreshRows,
  })

  // Miesiac dla przeplywu z domykaniem nagrod. useSaveAndCalculate liczy dokladnie jeden
  // miesiac, wiec przy kilku zaznaczonych miesiacach zostaje dotychczasowa,
  // jednoprzebiegowa sciezka zadaniowa (handleCalculate + TaskContext).
  const adoptPeriod = React.useMemo(() => {
    let months = period.months
    let year = period.year
    if (months.length === 0) {
      // Month AND year from the same source: selectedMonthKey is "MM-YYYY", and
      // period.year can lag behind the viewed year — pairing the key's month with
      // period.year could silently calculate the wrong year (the delete flow
      // already parses both from the normalized key for the same reason).
      const normalized = normalizeMonthKey(selectedMonthKey)
      const [monthStr, yearStr] = normalized.split('-')
      const parsedMonth = parseInt(monthStr, 10)
      const parsedYear = parseInt(yearStr, 10)
      if (isNaN(parsedMonth) || isNaN(parsedYear)) return null
      months = [parsedMonth]
      year = parsedYear
    }
    if (months.length !== 1) return null
    return { month: months[0], year }
  }, [period, selectedMonthKey])

  // Te same trwale flagi silnika co ekran bonusAndRewards i widok pojedynczego kierowcy
  // (klucz 'bonus-reward-toolbar') - "Przelicz" z kazdego ekranu wysyla identyczny zestaw.
  const { state: toolbarState } = useBonusRewardsToolbar()
  const domesticBusinessTripDateType =
    toolbarState.domesticBusinessTripDateType ?? undefined

  const finishAdoptCalculation = React.useCallback(() => {
    setIsCalculating(false)
    fetchGroupedTimesheets()
    // Amounts preview serves persisted TimesheetAmounts — stale until the job wrote them.
    queryClient.invalidateQueries({ queryKey: ['amounts-preview'] })
    queryClient.invalidateQueries({ queryKey: ['foreign-amounts-preview'] })
  }, [fetchGroupedTimesheets, queryClient])

  const saveAndCalculate = useSaveAndCalculate({
    companyId,
    month: adoptPeriod?.month ?? 1,
    year: adoptPeriod?.year ?? viewedYear,
    domesticBusinessTripDateType,
    onSuccess: () => finishAdoptCalculation(),
    onError: () => finishAdoptCalculation(),
  })

  /**
   * Wiersze + konfiguracje premii dla przebiegu 1, budowane z TEGO SAMEGO zrodla co ekran
   * bonusAndRewards i widok pojedynczego kierowcy (groupedTimesheet z showBonusConfiguration
   * + pickReward) - patrz komentarz przy buildCalculationInput w Single.tsx: wysylamy tylko
   * pola, ktore CalculateTimesheetJob nadpisuje bezwarunkowo, zeby niczego nie wyzerowac.
   * Zwraca null (=> sciezka zadaniowa bez domykania), gdy konfiguracji nie da sie odczytac.
   */
  const buildCalculationInputs = React.useCallback(
    async (driverNumbers: number[]) => {
      if (!adoptPeriod) return null
      const { month, year } = adoptPeriod

      // Oczekiwane netto z EasyTMS/BFM - best-effort, jak na pozostalych ekranach.
      const easyTmsNettoById = new Map<number, number>()
      try {
        const { data: nettoRows } = await fetchExpectedNettoFromEasyTms({
          companyId,
          year,
          month,
          driverIds: driverNumbers,
        })
        ;(nettoRows || []).forEach((r) => {
          if (r.found && r.expectedNettoAmount != null) {
            easyTmsNettoById.set(r.driverId, r.expectedNettoAmount)
          }
        })
      } catch (err) {
        console.error('EasyTMS expected netto fetch error:', err)
        toast.error({ text: t('timesheet.easytms-fetch-error') })
      }

      let rows: PayrollDriverRow[]
      try {
        const response = await getGroupedTimesheet({
          companyId,
          driversIds: driverNumbers,
          year,
          month,
          showBonusConfiguration: true,
          domesticBusinessTripDateType,
        })
        rows = Array.isArray(response.data) ? response.data : []
      } catch (err) {
        // Bez odczytanej konfiguracji nie da sie wyslac zadnego pola, nie niszczac
        // reszty kwot - liczymy dotychczasowa sciezka.
        console.error('Bonus configuration fetch error:', err)
        return null
      }

      const rowById = new Map(rows.map((r) => [r.driver.id, r]))
      const inputs: {
        id: number
        bonusConfiguration: Record<string, unknown>
      }[] = []
      for (const id of driverNumbers) {
        const row = rowById.get(id)
        // Kierowca bez wiersza dostalby pusta konfiguracje, ktora wyzerowalaby mu nagrode -
        // wtedy caly zestaw idzie bezpieczna sciezka zadaniowa.
        if (!row) return null

        const easyTmsNetto = easyTmsNettoById.get(id)
        // Netto wysylamy TYLKO gdy je mamy - wyslane 0 job potraktowalby jak cel rowny zeru.
        const expectedNettoAmount =
          easyTmsNetto ??
          (row.expectedNettoAmount != null && row.expectedNettoAmount > 0
            ? Number(row.expectedNettoAmount)
            : undefined)

        inputs.push({
          id,
          bonusConfiguration: {
            driverId: id,
            ...(expectedNettoAmount !== undefined && { expectedNettoAmount }),
            rewardRate: pickReward(row),
            bonusRate: row.bonus != null ? Number(row.bonus) : 0,
            useValuesFromContract: false,
            bailiffDeductionAmount: row.bailiffDeductionAmount ?? null,
            timesheetAdditionForLeaves: row.timesheetAdditionForLeaves ?? [],
          },
        })
      }

      if (easyTmsNettoById.size > 0) {
        toast.success({
          text: t('timesheet.easytms-fetch-success').replace(
            '{0}',
            String(easyTmsNettoById.size),
          ),
        })
      }

      return inputs
    },
    [adoptPeriod, companyId, domesticBusinessTripDateType, t],
  )

  const performCalculation = React.useCallback(
    async (excludedDriverIds?: number[]) => {
      setIsCalculating(true)
      try {
        // Pojedynczy miesiac idzie tym samym dwuprzebiegowym przeplywem co ekran
        // bonusAndRewards (przebieg 1 + nalozenie propozycji nagrod domykajacych
        // wyplate do oczekiwanego netto).
        if (adoptPeriod) {
          const excluded = new Set((excludedDriverIds ?? []).map(String))
          const driverNumbers = sideCheckedIds
            .map((id) => String(id))
            .filter((id) => !excluded.has(id))
            .map((id) => parseInt(id, 10))
            .filter((id) => !isNaN(id))

          if (driverNumbers.length > 0) {
            const inputs = await buildCalculationInputs(driverNumbers)
            if (inputs) {
              saveAndCalculate.mutate({
                drivers: inputs,
                automaticOvertimeCompensation:
                  toolbarState.automaticOvertimeCompensation,
                zusPaidFromAverageGross: toolbarState.zusPaidFromAverageGross,
              })
              // isCalculating gasi onSuccess/onError przez finishAdoptCalculation.
              return
            }
          }
        }

        const taskStarted = await handleCalculate(excludedDriverIds)

        if (!taskStarted) {
          setIsCalculating(false)
        }
      } catch (error) {
        console.error('Error during calculation:', error)
        setIsCalculating(false)
      }
    },
    [
      handleCalculate,
      adoptPeriod,
      sideCheckedIds,
      buildCalculationInputs,
      saveAndCalculate,
      toolbarState.automaticOvertimeCompensation,
      toolbarState.zusPaidFromAverageGross,
    ],
  )

  const findMissingContracts = React.useCallback((): {
    list: { driverId: number; driverName: string; months: number[] }[]
    allMissing: boolean
  } | null => {

    if (period.year !== viewedYear) return null

    let monthsToCheck: number[]
    if (period.months.length > 0) {
      monthsToCheck = period.months
    } else {
      const normalized = normalizeMonthKey(selectedMonthKey)
      const [monthStr] = normalized.split('-')
      const parsed = parseInt(monthStr, 10)
      if (isNaN(parsed)) return null
      monthsToCheck = [parsed]
    }

    const driverIds = sideCheckedIds.map(String)
    if (driverIds.length === 0) return null

    const list: {
      driverId: number
      driverName: string
      months: number[]
    }[] = []
    for (const driverId of driverIds) {
      const row = data.find((d) => d.id === driverId)
      if (!row || !row.hasContract) continue
      const missing = monthsToCheck.filter(
        (m) => row.hasContract![m - 1] === 0,
      )
      if (missing.length > 0) {
        list.push({
          driverId: Number(driverId),
          driverName: row.driverName,
          months: missing,
        })
      }
    }

    if (list.length === 0) return null

    const allMissing =
      list.length === driverIds.length &&
      list.every((entry) => entry.months.length === monthsToCheck.length)

    return { list, allMissing }
  }, [period, viewedYear, selectedMonthKey, sideCheckedIds, data])

  const handleCalculateWithLoading = React.useCallback(async () => {
    const missing = findMissingContracts()
    if (missing) {
      setMissingContracts(missing)
      return
    }
    await performCalculation()
  }, [findMissingContracts, performCalculation])

  const handleConfirmCalculateAnyway = React.useCallback(async () => {
    const excluded = missingContracts?.list.map((entry) => entry.driverId) ?? []
    setMissingContracts(null)
    await performCalculation(excluded)
  }, [missingContracts, performCalculation])

  const monthNameKeys = [
    'monthName.january',
    'monthName.february',
    'monthName.march',
    'monthName.april',
    'monthName.may',
    'monthName.june',
    'monthName.july',
    'monthName.august',
    'monthName.september',
    'monthName.october',
    'monthName.november',
    'monthName.december',
  ]

  // Human month label for the delete confirmation ("za miesiac lipiec" instead
  // of a dangling "za miesiac ?").
  const deleteMonthsLabel = React.useMemo(() => {
    const months =
      period.year === viewedYear && period.months.length > 0
        ? period.months
        : (() => {
            const [monthStr] = normalizeMonthKey(selectedMonthKey).split('-')
            const parsed = parseInt(monthStr, 10)
            return isNaN(parsed) ? [] : [parsed]
          })()
    return months.map((m) => t(monthNameKeys[m - 1])).join(', ')
  }, [period, viewedYear, selectedMonthKey, t])

  const missingContractsContent = React.useMemo<React.ReactNode[]>(() => {
    if (!missingContracts) return []
    return missingContracts.list.map((entry, idx) => (
      <React.Fragment key={`${entry.driverName}-${idx}`}>
        {entry.driverName}:{' '}
        <strong>
          {entry.months.map((m) => t(monthNameKeys[m - 1])).join(', ')}
        </strong>
      </React.Fragment>
    ))
  }, [missingContracts, t])

  // The delete flow must follow the same selection the user sees: table-row
  // selection when present, otherwise the sidebar checkboxes (the payroll view
  // already works this way — this view used to send only the table selection,
  // which is empty when drivers are picked in the sidebar, so removeTimesheets
  // went out with driverIds: [] and silently deleted nothing).
  const effectiveDeleteIds = React.useMemo(
    () => (selectedGroup?.ids.length ? selectedGroup.ids : sideCheckedIds),
    [selectedGroup, sideCheckedIds],
  )

  const handleDelete = React.useCallback(() => {
    setShowDeleteModal(true)
  }, [])

  const handleConfirmDelete = React.useCallback(async () => {
    const monthsToDelete =
      period.months.length > 0
        ? period.months.map((month) => ({ month, year: period.year }))
        : (() => {
            const normalized = normalizeMonthKey(selectedMonthKey)
            const [monthStr, yearStr] = normalized.split('-')
            return [
              { month: parseInt(monthStr, 10), year: parseInt(yearStr, 10) },
            ]
          })()

    // Close the dialog BEFORE awaiting: the removal takes minutes and the progress
    // overlay over the table is the feedback surface, not a frozen confirm modal.
    setShowDeleteModal(false)

    try {
      // Settlement periods can span a year boundary — one task per year.
      const monthsByYear = new Map<number, number[]>()
      for (const { month, year } of monthsToDelete) {
        monthsByYear.set(year, [...(monthsByYear.get(year) ?? []), month])
      }
      for (const [year, months] of monthsByYear) {
        await removeTimesheets({
          companyId,
          driverIds: effectiveDeleteIds,
          months,
          year,
        })
      }
      toast.success({ text: t('timesheet.timesheet-deleted') })
      resetAll()
      fetchGroupedTimesheets()
    } catch {
      toast.error({ text: t('timesheet.delete-error') })
    }
  }, [
    period,
    selectedMonthKey,
    effectiveDeleteIds,
    companyId,
    removeTimesheets,
    resetAll,
    fetchGroupedTimesheets,
    t,
  ])

  const selectedDriverNames = React.useMemo(() => {
    if (!effectiveDeleteIds.length) {
      return []
    }
    return drivers
      .filter((driver) => effectiveDeleteIds.includes(driver.id))
      .map((driver) => driver.fullName || `${driver.surname} ${driver.name}`)
  }, [effectiveDeleteIds, drivers])

  const resolvedDateForPrints = resolveDate(
    { year: Number(year) },
    { capDate: true },
  )
  // Szybkie wydruki biorą zaznaczone miesiące, jak WPF (GetSelectedMonths).
  const shortcutPrintRange =
    period.months.length > 0
      ? resolveDate(
          { year: period.year, months: period.months },
          { capDate: true },
        ).state.range
      : resolvedDateForPrints.state.range
  const printDriverIds =
    selectedGroup?.ids && selectedGroup.ids.length > 0
      ? selectedGroup.ids.map((id) => String(id))
      : sideCheckedIds.map((id) => String(id))

  return (
    <div className="tw-flex tw-flex-1 tw-flex-col tw-overflow-hidden tw-rounded-md">
      <TimeSheetActionBar
        companyId={companyId}
        onPrint={handlePrint}
        printShortcuts={
          <TimesheetPrintShortcuts
            companyId={companyId}
            driverIds={printDriverIds}
            dateFrom={shortcutPrintRange.dateFrom}
            dateTo={shortcutPrintRange.dateTo}
          />
        }
        onDelete={handleDelete}
        onRefresh={handleRefresh}
        onCalculate={handleCalculateWithLoading}
        calculateBusy={scopeBusy.busy && scopeBusy.byOther}
        calculateBusyText={
          scopeBusy.startedBy
            ? `${scopeBusy.startedBy} — ${t('timesheet.calculation-in-progress')}`
            : undefined
        }
        period={period}
        setPeriod={setPeriod}
      />
     

      <div className="tw-relative tw-flex tw-min-h-0 tw-flex-1 tw-flex-col tw-overflow-hidden tw-rounded-b-md">
        {/* Table-scoped progress overlay — same pattern as the bonusAndRewards grid
            (Loader over the results area), instead of a full-screen modal. Also the
            only progress surface for the delete flow. */}
        {(isCalculating || isRemovingTimesheets) && (
          <Loader
            className="tw-z-50 !tw-flex-col !tw-justify-start tw-p-10"
            fill
            position="absolute"
            text={
              isRemovingTimesheets
                ? removalStatusText || 'Trwa usuwanie ewidencji...'
                : saveAndCalculate.statusText ||
                  `Trwa przeliczanie ewidencji...${runningCalculationProgress}`
            }
            shadow
            spinnerClassName="tw-mt-[20vh]"
          />
        )}
        {showCalculateView ? (
          <CalculateRecordsView
            onImportFile={handleImportFile}
            onCalculateFromData={handleCalculateFromData}
          />
        ) : (
          <TimesheetTable
            data={filteredData}
            viewMode={viewMode}
            showBonusFields={showBonusFields}
            onDriverClick={(driverId) => {
              const normalizedMonthKey = normalizeMonthKey(selectedMonthKey)
              const date = formatDateFromMonth(normalizedMonthKey)
              handleDriverClick(driverId, date)
            }}
            companyId={companyId}
            loading={loading}
            selectedYear={(() => {
              const normalizedMonthKey = normalizeMonthKey(selectedMonthKey)
              if (
                !normalizedMonthKey ||
                typeof normalizedMonthKey !== 'string'
              ) {
                return new Date().getFullYear()
              }
              const [monthStr, yearStr] = normalizedMonthKey.split('-')
              const year = parseInt(yearStr, 10)
              return isNaN(year) ? new Date().getFullYear() : year
            })()}
          />
        )}
      </div>

      {foreignTripConfirmModal}
      {outOfDateRateConfirmModal}

      {/* Missing contract warning modal */}
      <ConfirmModal
        isOpen={missingContracts !== null}
        onClose={() => setMissingContracts(null)}
        title={t('timesheet.insufficient-data-title')}
        text={t('timesheet.contract-not-found-title')}
        content={missingContractsContent}
        actionText={t('timesheet.calculate-for-others')}
        onConfirm={
          missingContracts?.allMissing
            ? undefined
            : handleConfirmCalculateAnyway
        }
      />

      {/* Delete confirmation modal */}
      <ConfirmModal
        isOpen={showDeleteModal}
        onClose={() => setShowDeleteModal(false)}
        title={t('timesheet.delete-confirmation-title')}
        text={
          effectiveDeleteIds.length === 1
            ? t('timesheet.delete-confirmation-single')
                .replace('{0}', selectedDriverNames[0] ?? '')
                .replace('{1}', deleteMonthsLabel)
            : t('timesheet.delete-confirmation-multiple')
                .replace('{0}', String(effectiveDeleteIds.length))
                .replace('{1}', deleteMonthsLabel)
        }
        content={effectiveDeleteIds.length > 1 ? selectedDriverNames : undefined}
        actionText={t('common.delete')}
        onConfirm={handleConfirmDelete}
      />

      {/* Calculate Records Modal */}
      <CalculateRecordsModal
        isOpen={showCalculateRecordsModal}
        onClose={() => setShowCalculateRecordsModal(false)}
        companyId={companyId}
        selectedMonth={selectedMonthKey}
        onCalculateSuccess={handleCalculateSuccess}
      />

      {/* Print Modal */}
      <PrintActionContainer
        companyId={companyId}
        dateFrom={resolvedDateForPrints.state.range.dateFrom}
        dateTo={resolvedDateForPrints.state.range.dateTo}
        driverIds={
          selectedGroup?.ids && selectedGroup.ids.length > 0
            ? selectedGroup.ids.map((id) => String(id))
            : sideCheckedIds.map((id) => String(id))
        }
        availableReports={availableReports}
        isOpen={showPrintModal}
        setOpen={setShowPrintModal}
      />
    </div>
  )
}

export const Group = GroupComponent

export default Group
